import { DragEvent, FC } from 'react';

import { Button, Grid, VisuallyHidden } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { close } from '@purpur/library/icon/assets/close';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ProductSearchItem } from '@telialt/core-search/src/model';
import { ProductsCompareProduct } from '@telialt/eshop-comparison/src/models';
import { withParams } from '@telialt/utils/src/helpers';
import { getComparisonPagePath } from '@telialt/utils/src/pathUtils';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { ESHOP_CATEGORY_TEST_ID } from '../../../constants/test-ids';
import { ComparedProductCard } from './ComparedProductCard';
import {
    CompareActionsContainer,
    CompareButton,
    ComparedProductPlaceholder,
    ComparedProductsContainer,
    ComparedProductsWrapper,
} from './ComparedProducts.styles';

type Props = {
    segment: TeliaSegment;
    categoryPath: string;
    productsLimit: number;
    products: ProductsCompareProduct[];
    onAddProduct: (product: ProductSearchItem) => void;
    onRemoveProduct: (productCode: string) => void;
    onRemoveAllProducts: () => void;
};

export const ComparedProducts: FC<Props> = ({
    segment,
    categoryPath,
    productsLimit,
    products,
    onAddProduct,
    onRemoveProduct,
    onRemoveAllProducts,
}) => {
    const { t } = useTranslations();

    const handleProductDrop = (event: DragEvent) => {
        const rawProduct = event.dataTransfer.getData('product');
        try {
            const parsedProduct = JSON.parse(rawProduct);
            onAddProduct(parsedProduct);
        } catch {
            /* No op */
        }
    };

    return (
        <ComparedProductsWrapper
            $isHidden={!products?.length}
            id="compared-products"
            aria-live="polite"
            aria-atomic="true"
            aria-labelledby="compared-products-title"
        >
            <VisuallyHidden>
                <h2 id="compared-products-title">{t['eshop.category.compare.title']}</h2>
            </VisuallyHidden>
            {!!products?.length && (
                <Grid>
                    <ComparedProductsContainer>
                        {products.map((product) => (
                            <ComparedProductCard
                                key={product.id}
                                product={product}
                                url={product.url}
                                onRemove={() => onRemoveProduct(product.id)}
                            />
                        ))}
                        {products.length < productsLimit &&
                            Array(productsLimit - products.length)
                                .fill(undefined)
                                .map((_, index) => (
                                    <ComparedProductPlaceholder
                                        key={`placeholder-${index}`}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={handleProductDrop}
                                        aria-hidden="true"
                                    >
                                        {withParams(t['eshop.category.compare.placeholder'], {
                                            count: products.length + index + 1,
                                        })}
                                    </ComparedProductPlaceholder>
                                ))}
                        <CompareActionsContainer>
                            <CompareButton
                                variant="primary"
                                href={getComparisonPagePath(segment, categoryPath)}
                                dataTest={ESHOP_CATEGORY_TEST_ID.CATEGORY_COMPARE_BUTTON}
                                disabled={products.length < 2}
                            >
                                {t['eshop.category.compare.submit']}
                            </CompareButton>
                            <Button
                                variant="text"
                                onClick={onRemoveAllProducts}
                                data-test={ESHOP_CATEGORY_TEST_ID.CATEGORY_COMPARE_REMOVE_ALL_BUTTON}
                            >
                                <Icon svg={close} size="xs" />
                                {t['eshop.category.compare.remove_all']}
                            </Button>
                        </CompareActionsContainer>
                    </ComparedProductsContainer>
                </Grid>
            )}
        </ComparedProductsWrapper>
    );
};
